import { useEffect, useMemo, useState } from "react";
import { vercelAnalyticsService, type AnalyticsRange, type AnalyticsSummary, type PageSummary, type RegionSummary } from "@/services/vercelAnalyticsService";

interface UseVercelAnalyticsResult {
  summary?: AnalyticsSummary;
  regions: RegionSummary[];
  pages: PageSummary[];
  loading: boolean;
  error?: string;
}

export function useVercelAnalytics(range: AnalyticsRange = "7d"): UseVercelAnalyticsResult {
  const [summary, setSummary] = useState<AnalyticsSummary | undefined>();
  const [regions, setRegions] = useState<RegionSummary[]>([]);
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    async function loadAnalytics() {
      setLoading(true);
      setError(undefined);

      try {
        const data = await vercelAnalyticsService.getOverview(range, controller.signal);
        if (!isMounted) return;

        setSummary(data.summary);
        setRegions(data.regions);
        setPages(data.pages);
      } catch (err: any) {
        if (!isMounted) return;
        console.error("Erro carregando analytics da Vercel", err);
        setError(err?.message || "Não foi possível carregar os dados de analytics da Vercel");
        setSummary(undefined);
        setRegions([]);
        setPages([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadAnalytics();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [range]);

  const sortedRegions = useMemo(() => {
    if (!summary) return regions;
    return regions.map(region => ({
      ...region,
      percentage: summary.totalVisits > 0 ? Number(((region.visits / summary.totalVisits) * 100).toFixed(2)) : 0,
    }));
  }, [regions, summary]);

  return {
    summary,
    regions: sortedRegions,
    pages,
    loading,
    error,
  };
}

export type { AnalyticsRange };
